import React, { useCallback } from 'react';
import { Autocomplete, Checkbox, Chip, FormControlLabel, TextField, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import useData from './hook';
import { NotificationQcFieldName } from './const';
import QcTooltip from '50.quickConnect.Fields/QcTooltip';
import { ValueFieldsTypesProps } from '50.quickConnect.Fields/FieldsTypes/types';
import { NotificationDesc, NotificationTarget } from '90.quickConnect.Models/models';
import { DataSourceType } from '90.quickConnect.Models/enums';
import { FieldMainBox, FlexRow } from '50.quickConnect.Fields/styles';
import { LruToggleButton, LruHistoryPanel } from '50.quickConnect.Fields/LRU';
import AutoCompleteSearchButton from '50.quickConnect.Fields/AutoCompleteSearchButton';
import { OptionsRender } from '50.quickConnect.Fields/FieldsTypes/Inputs/NotificationQcField/types';

const NotificationQcField = ({
  field,
  updateDeclaration,
  labelSpan,
  isReadOnly,
  labelClass,
  selectedIndex,
  setSelectedIndex,
}: ValueFieldsTypesProps) => {
  const notificationDesc = field as NotificationDesc;
  const { id, dataSource, dataSourceType, fullPathId, toolTip } = notificationDesc;

  const nameWithId = `${NotificationQcFieldName}-${id}`;

  const {
    autoCompleteProp,
    localValue,
    handleChange,
    t,
    showAutoSendToMe,
    notificationData,
    handleOnChangeCheckBox,
    handleOnChangeSearchAllUO,
    searchNow,
    isDataSourceAADOrFamilly,
    lruList,
    handleDeleteItem,
    toggleShowLruList,
    showLruList,
    handleOnInputChange,
    valueToDisplay,
    open,
    onTagChange,
  } = useData(notificationDesc, updateDeclaration, selectedIndex, setSelectedIndex);
  const { breakpoints } = useTheme();

  const handleSelectField = useCallback(() => {
    if (!isReadOnly) setSelectedIndex(fullPathId);
  }, [isReadOnly, setSelectedIndex, fullPathId]);

  return (
    <Box
      className={labelClass}
      data-cy={nameWithId}
      sx={FieldMainBox(breakpoints)}
      id={`scrollToAnchor-${fullPathId}`}
      onClick={handleSelectField}
      onFocus={handleSelectField}
    >
      <>
        <Box sx={FieldMainBox(breakpoints)}>
          <Box sx={FlexRow}>
            <Box component="span" sx={{ flex: 1 }}>
              {labelSpan}
            </Box>
            {toolTip && <QcTooltip parentId={id} tooltip={toolTip} />}
          </Box>
        </Box>
        {showAutoSendToMe && (
          <FormControlLabel
            sx={{ mb: '1.3em' }}
            control={
              <Checkbox
                checked={notificationData.sendToMe}
                data-cy={`${nameWithId}-checkbox`}
                onChange={handleOnChangeCheckBox}
              />
            }
            label={t('formlib_mail_send_me')}
          />
        )}
        {dataSourceType === DataSourceType.Family &&
          dataSource &&
          dataSource.idDataSource &&
          dataSource.forceSearchAllUO === undefined && (
            <FormControlLabel
              sx={{ mb: '1.3em' }}
              control={
                <Checkbox
                  checked={notificationData.searchAllUO}
                  data-cy={`${nameWithId}-search-all-uo-checkbox`}
                  onChange={handleOnChangeSearchAllUO}
                  disabled={isReadOnly}
                />
              }
              label={t('formlib_mail_info_datasource_search_all_uo')}
              disabled={isReadOnly}
            />
          )}
        <Box sx={FlexRow}>
          <Autocomplete
            sx={{ flex: 1 }}
            multiple
            id={nameWithId}
            limitTags={3}
            onChange={(event, value, reason, details) => {
              handleChange(event, value, reason, details);
            }}
            freeSolo={true}
            inputValue={localValue}
            disabled={isReadOnly}
            onInputChange={handleOnInputChange}
            value={valueToDisplay}
            options={autoCompleteProp}
            // filterOptions={isDataSourceAADOrFamilly ? (x) => x : undefined}
            // open={open}
            open={open}
            getOptionLabel={(option: any) =>
              !dataSource?.forcedSelection && typeof option === 'string' ? option : option?.value ?? 'rien'
            }
            filterOptions={(x) => x}
            renderOption={(props, option) => (
              <li
                {...props}
                key={(option as OptionsRender).id}
                id={(option as OptionsRender).id}
                data-cy={`${nameWithId}-li-${(option as OptionsRender).id}`}
              >
                <div>
                  {option.title}
                  <div>{option.description}</div>
                </div>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option: string | NotificationTarget, index) => {
                return (
                  //eslint-disable-next-line react/jsx-key
                  <Chip
                    label={typeof option === 'string' ? option : option.target}
                    data-cy={`${nameWithId}-chip-${index}`}
                    {...getTagProps({ index })}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} label={t('formlib_mail_hint')} placeholder={t('formlib_mail_hint').toString()} />
            )}
          />
          {isDataSourceAADOrFamilly && (
            <AutoCompleteSearchButton dataCy={`${nameWithId}-search`} onClick={searchNow} isReadOnly={!!isReadOnly} />
          )}
          <LruToggleButton hasItems={lruList.length > 0} onClick={toggleShowLruList} disabled={isReadOnly} />
        </Box>
        <LruHistoryPanel
          disabled={isReadOnly}
          show={showLruList}
          list={lruList}
          onTagChange={onTagChange}
          handleDeleteItem={handleDeleteItem}
          fieldFullId={fullPathId}
        />
      </>
    </Box>
  );
};

export default NotificationQcField;
